import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Button,
  Divider,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { 
  Rocket as RocketIcon,
  ViewModule as ViewModuleIcon,
  Construction as ConstructionIcon,
  Diversity3 as Diversity3Icon,
  Explore as ExploreIcon,
  AccountBalance as AccountBalanceIcon,
  Build as BuildIcon,
  ArrowForward as ArrowForwardIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import GradientText from '../common/GradientText.tsx';
import MainFunctionCards from '../../components/problemWorkspace/MainFunctionCards.tsx';
import { 
  Users,
  Package,
  GitBranch,
  DollarSign,
  //FileText as DocIcon,
  Settings as EnhanceIcon,
  Users as SurrogatesIcon,
  PlayCircle as DemoIcon,
  Edit as EditIcon,
  FileCheck as SolutionsIcon,
  FileCode as CodeIcon,
  MessageSquare as ForumIcon,
  BookOpen as CaseStudyIcon,
  TrendingUp as MetricsIcon,
  Target as CriteriaIcon,
  //BarChart as DashboardIcon,
  FileText as RFPIcon
} from 'lucide-react';
import StepCard from './StepCard.tsx';
import BenefitCard from './BenefitCard.tsx';
import FeatureCard from './FeatureCard.tsx';

            //<Grid item xs={12} md={6}>
              //<FeatureCard
                //title="Executive Surrogates"
                //description="Leverage AI-powered Executive Surrogates to gain personalized insights, recommendations, and expert-level support."
                //emoji="🚀"
              //>
            //</Grid>

interface WelcomeGuideProps {
  onGetStarted: () => void;
  onOpenExecutives?: () => void;
  onSelectFunction?: (functionId: string) => void;
  onOpenRefineMenu?: (event: React.MouseEvent<HTMLElement>) => void;
  onOpenEnhanceMenu?: (event: React.MouseEvent<HTMLElement>) => void;
  onOpenSolutions?: () => void;
  onOpenPlan?: () => void;
  onOpenToolbox?: () => void;
  onOpenSurrogates?: () => void;
  onOpenDemo?: () => void;
  onOpenProposals?: () => void;
  onOpenFeedback?: () => void;
  onOpenAttribution?: () => void;
  onShowComingSoon?: (message: string) => void;
  onOpenNewProposal?: () => void;
  onOpenSubStepper?: (type: string) => void;
}

const WelcomeContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
  '@keyframes gradient': {
    '0%': {
      backgroundPosition: '0% 50%'
    },
    '50%': {
      backgroundPosition: '100% 50%'
    },
    '100%': {
      backgroundPosition: '0% 50%'
    }
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.1)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(138, 43, 226, 0.2)',
    border: '1px solid rgba(138, 43, 226, 0.3)',
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: 'rgba(138, 43, 226, 0.1)',
  marginRight: theme.spacing(2),
  color: '#8A2BE2',
}));

const GetStartedButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
  color: '#fff',
  padding: theme.spacing(1, 4),
  borderRadius: theme.spacing(5),
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
    boxShadow: '0 0 15px rgba(138, 43, 226, 0.5)',
    transform: 'translateY(-2px)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  background: 'rgba(138, 43, 226, 0.1)',
  color: '#00CC99',
  borderRadius: theme.spacing(4),
  padding: theme.spacing(0.5, 2),
  fontSize: '0.8rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.2)',
    transform: 'translateY(-2px)',
  },
  '& .MuiButton-endIcon': {
    marginLeft: 4,
    fontSize: '1rem',
  }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50px',
  right: '40px',
  width: '60px',
  height: '60px',
  opacity: 0.8,
  transition: 'all 0.3s ease',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50px',
    padding: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },

  '&:hover::before': {
    opacity: 1,
  },
  
  '&:hover .logo-image': {
    transform: 'scale(1.05)',
    boxShadow: '0 0 15px rgba(138, 43, 226, 0.7)',
  }
}));

const RoundImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  objectFit: 'cover',
}));

const mainFunctionOptions = [
  {
    id: 'people',
    label: 'People',
    description: 'Managing teams, improving leadership, and building a collaborative culture.',
    icon: <Users size={24} color="#FF6F91" />,
    component: 'ExecutiveCard'
  },
  {
    id: 'product',
    label: 'Product',
    description: 'Ensuring your offering meets market needs, from ideation to commercialization.',
    icon: <Package size={24} color="#00CC99" />,
    component: 'ProductStrategyStepper'
  },
  {
    id: 'process',
    label: 'Process',
    description: 'Streamlining operations and creating reliable systems.',
    icon: <GitBranch size={24} color="#FFD700" />,
    component: 'ProposalDetailContent'
  },
  {
    id: 'profit',
    label: 'Profit',
    description: 'Navigating complex financials and making sense of fluctuating revenue streams.',
    icon: <DollarSign size={24} color="#8A2BE2" />,
    component: 'BusinessPlanStepper'
  }
];

const WelcomeGuide: React.FC<WelcomeGuideProps> = ({ 
  onGetStarted,
  onOpenExecutives,
  onSelectFunction,
  onOpenRefineMenu,
  onOpenEnhanceMenu,
  onOpenSolutions,
  onOpenPlan,
  onOpenToolbox,
  onOpenSurrogates,
  onOpenDemo,
  onOpenProposals,
  onOpenFeedback,
  onOpenAttribution,
  onShowComingSoon,
  onOpenNewProposal,
  onOpenSubStepper
}) => {
  // Helper function to combine the guide dismissal with any other action
  const handleAction = (callback?: Function, ...args: any[]) => {
    // First dismiss the guide
    onGetStarted();
    
    // Then execute the callback if provided
    if (callback) {
      callback(...args);
    }
  };

  // Function to handle "Coming Soon" features
  const handleComingSoon = (message: string) => {
    if (onShowComingSoon) {
      onShowComingSoon(message);
    } else {
      // Fallback if onShowComingSoon not provided
      alert(`Coming soon! ${message}`);
    }
    onGetStarted();
  };

  // When handling executive selection in WelcomeGuide
  const handleExecutiveSelect = () => {
    // First dismiss the guide
    onGetStarted();
    
    // Then open executives drawer if the callback exists
    if (onOpenExecutives) {
      onOpenExecutives();
    }
  };

  // Function to handle opening the plan
  const handleOpenPlan = () => {
    onGetStarted();
    if (onOpenPlan) {
      onOpenPlan();
    } else if (onSelectFunction) {
      onSelectFunction('plan');
    }
  };

  // Function to handle opening the toolbox
  const handleOpenToolbox = () => {
    onGetStarted();
    if (onOpenToolbox) {
      onOpenToolbox();
    } else if (onSelectFunction) {
      onSelectFunction('toolbox');
    }
  };

  // Function to handle opening surrogates
  const handleOpenSurrogates = () => {
    onGetStarted();
    if (onOpenSurrogates) {
      onOpenSurrogates();
    } else if (onSelectFunction) {
      onSelectFunction('surrogates');
    }
  };

  // Function to handle opening the demo
  const handleOpenDemo = () => {
    onGetStarted();
    if (onOpenDemo) {
      onOpenDemo();
    } else if (onSelectFunction) {
      onSelectFunction('savvyDemo');
    }
  };

  // Function to handle opening proposals
  const handleOpenProposals = () => {
    onGetStarted();
    if (onOpenProposals) {
      onOpenProposals();
    } else if (onSelectFunction) {
      onSelectFunction('proposals');
    }
  };

  // Function to handle opening attribution
  const handleOpenAttribution = () => {
    onGetStarted();
    if (onOpenAttribution) {
      onOpenAttribution();
    } else {
      // Navigate to attribution page
      window.location.href = '/attribution';
    }
  };

  // Function to handle opening feedback
  const handleOpenFeedback = () => {
    onGetStarted();
    if (onOpenFeedback) {
      onOpenFeedback();
    } else if (onSelectFunction) {
      onSelectFunction('feedback');
    }
  };

  const iconSize = 36;

  // Function to handle opening sub-steppers
  const handleOpenSubStepper = (type: string) => {
    onGetStarted();
    if (onOpenSubStepper) {
      onOpenSubStepper(type);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <WelcomeContainer elevation={3}>
        <LogoContainer>
          <RoundImage 
            src="/images/The_Eterna_Cognitive_Nexus.png"
            alt="Eterna Nexus" 
            width="100%" 
            height="100%"
            onClick={onGetStarted}
          />
        </LogoContainer>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <GradientText gradient="secondary" variant="h3" sx={{ mb: 1 }}>
            Level up your organization with AI
          </GradientText>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
              Running a small business means wearing many hats—
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 1 }}>
              <Chip 
                label="CEO" 
                clickable
                onClick={() => handleAction(onSelectFunction, 'executives')}
                sx={{ 
                  bgcolor: 'rgba(0, 255, 255, 0.3)', 
                  color: '#00CC99',
                  border: '1px solid rgba(0, 204, 153, 0.3)',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: 'rgba(138, 43, 226, 0.2)' }
                }} 
              />
              <Chip 
                label="CTO" 
                clickable
                onClick={() => handleAction(onSelectFunction, 'executives')}
                sx={{ 
                  bgcolor: 'rgba(138, 43, 226, 0.2)', 
                  color: '#00CC99',
                  border: '1px solid rgba(138, 43, 226, 0.3)',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: 'rgba(0, 255, 255, 0.3)' }
                }} 
              />
              <Chip 
                label="CPO" 
                clickable
                onClick={() => handleAction(onSelectFunction, 'executives')}
                sx={{ 
                  bgcolor: 'rgba(233, 30, 99, 0.2)', 
                  color: '#00CC99',
                  border: '1px solid rgba(233, 30, 99, 0.3)',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: 'rgba(0, 255, 255, 0.3)' }
                }} 
              />
              <Chip 
                label="CMO" 
                clickable
                onClick={() => handleAction(onSelectFunction, 'executives')}
                sx={{ 
                  bgcolor: 'rgba(255, 111, 0, 0.2)', 
                  color: '#00CC99',
                  border: '1px solid rgba(0, 119, 182, 0.3)',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: 'rgba(0, 255, 255, 0.3)' }
                }} 
              />
              <Chip 
                label="CFO" 
                clickable
                onClick={() => handleAction(onSelectFunction, 'executives')}
                sx={{ 
                  bgcolor: 'rgba(0, 119, 182, 0.2)', 
                  color: '#00CC99',
                  border: '1px solid rgba(255, 111, 0, 0.3)',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: 'rgba(0, 255, 255, 0.3)' }
                }} 
              />
            </Box>
            
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              and more—all while handling day-to-day operations. <br /> AI can empower small business owners to solve big challenges.
            </Typography>
          </Box>

          <Divider sx={{ my: 3, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
        </Box>

        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2, color: '#00CC99' }}>
            Understand How to Use <GradientText gradient="primary" variant="h5" sx={{ mb: 2 }}>
          Artificial Intelligence </GradientText> Effectively
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3 }}>
          Start harnessing AI to Grow Your Business.
          </Typography>

          <Box sx={{ textAlign: 'center' }}>
          <Divider sx={{ my: 3, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />

            <GetStartedButton 
              variant="contained" 
              size="large" 
              onClick={onGetStarted}
              endIcon={<RocketIcon />}
            >
              GET STARTED
            </GetStartedButton>
          </Box>
          <Divider sx={{ my: 3, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />

          {/* Five-Step Approach */}
          

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <StepCard
                title="Create"
                description="Define your challenge with precision. Provide a refined problem statement along with relevant documents, spreadsheets, slides, code, and strategic plans to shape a solid foundation."
                chipLabel="New Problem"
                icon={<ViewModuleIcon style={{ color: '#00CC99', fontSize: iconSize, marginRight: 8 }} />}
                details={[
                  {
                    text: 'Start by crafting a clear problem statement using multiple input methods—voice, text, document uploads, API connections, or links',
                    icon: <EditIcon size={iconSize} />,
                    action: 'newProposal'
                  },
                  {
                    text: 'Leverage AI suggestions to refine your problem by using the Problem Solving Toolbox',
                    icon: <EnhanceIcon size={iconSize} />,
                    action: 'toolbox'
                  },
                  {
                    text: 'Access versatile workspaces for drafting Proposals or Plans with documents, spreadsheets, slides, and code',
                    icon: <CodeIcon size={iconSize} />,
                    action: 'newProposal'
                  },
                  {
                    text: 'Generate and submit Requests for Proposals (RFPs) for tailored solutions',
                    icon: <RFPIcon size={iconSize} />,
                    action: 'solutions'
                  }
                ]}
                onClick={(action) => {
                  switch(action) {
                    case 'newProposal':
                      if (onOpenNewProposal) {
                        handleAction(onOpenNewProposal);
                      } else {
                        handleAction();
                      }
                      break;
                    case 'executives':
                      handleExecutiveSelect();
                      break;
                    case 'solutions':
                      if (onOpenSolutions) {
                        handleAction(onOpenSolutions);
                      } else if (onSelectFunction) {
                        handleAction(onSelectFunction, 'solutions');
                      }
                      break;
                    default:
                      handleAction();
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StepCard
                title="Forge"
                description="Refine AI prompts and problem-solving strategies. Develop a Request for Proposal (RFP) or a Detailed Plan that outlines your needs, ensuring you receive tailored solutions that align with your goals."
                chipLabel="Use Toolbox"
                icon={<ConstructionIcon style={{ color: '#00CC99', fontSize: iconSize, marginRight: 8 }} />}
                details={[
                  {
                    text: 'Edit the background, key principles, and project details of your challenge',
                    icon: <EnhanceIcon size={iconSize} />,
                    action: 'surrogates'
                  },
                  {
                    text: 'Utilize our Knowledge Toolbox with frameworks like SWOT Analysis, Five Whys, PDCA Cycle',
                    icon: <BuildIcon size={iconSize} />,
                    action: 'toolbox'
                  },
                  {
                    text: 'Collaborate with AI Surrogates—executive advisors specializing in various roles',
                    icon: <SurrogatesIcon size={iconSize} />,
                    action: 'surrogates'
                  }
                ]}
                onClick={(action) => {
                  switch(action) {
                    case 'surrogates':
                      handleOpenSurrogates();
                      break;
                    case 'toolbox':
                      handleOpenToolbox();
                      break;
                    default:
                      handleAction();
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StepCard
                title="Engage"
                description="Collaborate with AI Surrogates—your virtual executive advisors—to gain personalized insights, recommendations, and expert-level support. Our experts and community members are here to help you succeed."
                chipLabel="View Surrogates"
                icon={<Diversity3Icon />}
                details={[
                  {
                    text: 'Connect with AI-driven surrogates and human experts',
                    icon: <SurrogatesIcon size={18} />,
                    action: 'executives'
                  },
                  {
                    text: 'Engage with community forums and customer support channels',
                    icon: <ForumIcon size={18} />,
                    action: 'comingSoon'
                  },
                  {
                    text: 'Benefit from peer insights and expert mentoring',
                    icon: <SurrogatesIcon size={18} />,
                    action: 'comingSoon'
                  }
                ]}
                onClick={(action) => {
                  switch(action) {
                    case 'executives':
                      handleExecutiveSelect();
                      break;
                    case 'comingSoon':
                      handleComingSoon('Tell your friends about this exciting feature!');
                      break;
                    default:
                      handleAction();
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StepCard
                title="Explore"
                description="Discover similar challenges, proven solutions, recommendations,and insights, drawing from a vast knowledge base to refine your approach."
                chipLabel="Solution Demo"
                icon={<ExploreIcon />}
                details={[
                  {
                    text: 'Browse a curated selection of similar problems and solutions',
                    icon: <DemoIcon size={18} />,
                    action: 'demo'
                  },
                  {
                    text: 'Review active proposals and recommended surrogates',
                    icon: <SolutionsIcon size={18} />,
                    action: 'proposals'
                  },
                  {
                    text: 'Access case studies and best practices',
                    icon: <CaseStudyIcon size={18} />,
                    action: 'comingSoon'
                  }
                ]}
                onClick={(action) => {
                  switch(action) {
                    case 'demo':
                      handleOpenDemo();
                      break;
                    case 'proposals':
                      handleOpenProposals();
                      break;
                    case 'comingSoon':
                      handleComingSoon('Tell your friends about this exciting feature!');
                      break;
                    default:
                      handleAction();
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StepCard
                title="Valuation"
                description="Assess your project's impact, feasibility, scope, benefits, process comparison, and success criteria with dynamic, data-driven metrics to make informed decisions."
                chipLabel="View Solutions"
                icon={<AccountBalanceIcon />}
                details={[
                  {
                    text: 'Evaluate project scope, timeline, and budget using dynamic metrics',
                    icon: <MetricsIcon size={18} />,
                    action: 'feedback'
                  },
                  {
                    text: 'Define clear success criteria and Definition of Done',
                    icon: <CriteriaIcon size={18} />,
                    action: 'plan'
                  },
                  {
                    text: 'View Leaderboards and Contribution Attribution',
                    icon: <TrophyIcon fontSize="small" />,
                    action: 'attribution'
                  },
                  {
                    text: 'Compare your project with similar projects',
                    icon: <CriteriaIcon size={18} />,
                    action: 'proposals'
                  }
                ]}
                onClick={(action) => {
                  switch(action) {
                    case 'feedback':
                      handleOpenFeedback();
                      break;
                    case 'plan':
                      handleOpenPlan();
                      break;
                    case 'attribution':
                      handleOpenAttribution();
                      break;
                    case 'proposals':
                      handleOpenProposals();
                      break;
                    default:
                      handleAction();
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FeatureCard 
                title="Executive Perspectives"
                description="Get insights from CEO, CTO, CPO, CMO, and CFO viewpoints on your problem."
                icon={<Users size={24} />}
                onClick={() => handleExecutiveSelect()}
                extraButtons={[
                  {
                    label: "View Strategic Plan",
                    onClick: () => handleOpenSubStepper('strategic'),
                    color: "#00CC99"
                  },
                  {
                    label: "View Technology Roadmap",
                    onClick: () => handleOpenSubStepper('technology'),
                    color: "#8A2BE2"
                  },
                  {
                    label: "View Product Strategy",
                    onClick: () => handleOpenSubStepper('product'),
                    color: "#E91E63"
                  },
                  {
                    label: "View Marketing Plan",
                    onClick: () => handleOpenSubStepper('marketing'),
                    color: "#FF6F00"
                  },
                  {
                    label: "View Financial Plan",
                    onClick: () => handleOpenSubStepper('financial'),
                    color: "#00336"
                  },
                  {
                    label: "View Business Plan",
                    onClick: () => handleOpenSubStepper('business'),
                    color: "#00CC99"
                  }
                ]}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />

          {/* Toolbox Section */}
          <Grid item xs={12}>
            <FeatureItem>
              <IconWrapper>
                <BuildIcon />
              </IconWrapper>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff', textAlign: 'center' }}>
                  LEVERAGE THE KNOWLEDGE TOOLBOX
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Use AI-powered models and frameworks to optimize decision-making.
                </Typography>
                <ActionButton 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => handleOpenToolbox()}
                >
                  Open Toolbox
                </ActionButton>
              </Box>
            </FeatureItem>
          </Grid>

          <Grid container spacing={3}>
            {/* Main Function Cards */}
            <Box sx={{ p: 2, mt: 2 }}>
              <MainFunctionCards 
                onSelectFunction={(functionId) => {
                  // First dismiss the guide
                  onGetStarted();
                  
                  // Then select the function
                  if (onSelectFunction) {
                    onSelectFunction(functionId);
                  }
                }}
                selectedFunction={null}
                options={mainFunctionOptions}
              />
            </Box>
          </Grid>

          <Divider sx={{ my: 3, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />

          {/* Why Choose Section */}
          <Typography variant="h5" sx={{ mb: 2, color: '#00CC99' }}>
            Why Choose the Agentic AI Ecosystem?
          </Typography>

          <GradientText gradient="secondary" variant="h4">Empower Your Business with AI-Driven Success</GradientText>
          
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3 }}>
            Our ecosystem is built on the same foundational principles that have fueled the growth of countless successful businesses. By blending cutting-edge AI with proven business methodologies, we equip you with the tools to navigate complexity and unlock new opportunities.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <BenefitCard
                title="Bridge the Gap"
                description="Transform obstacles into opportunities by combining AI-powered insights with time-tested business strategies to drive clarity, efficiency, and growth."
                emoji="🔥"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <BenefitCard
                title="Increase Efficiency"
                description="Automate and streamline key processes, freeing up time so you can focus on leading, innovating, and scaling your business with confidence."
                emoji="⚡"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <BenefitCard
                title="Foster Collaboration"
                description="Join a dynamic community of entrepreneurs, experts, and AI-driven advisors, gaining invaluable insights and real-world solutions to fuel your success."
                emoji="🤝"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <BenefitCard
                title="Drive Innovation"
                description="Leverage AI-driven frameworks to analyze, optimize, and future-proof your business strategy, ensuring long-term, sustainable growth."
                emoji="🚀"
              />
            </Grid>
          </Grid>

          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 3, mb: 4 }}>
            By embedding these principles into your daily operations, you gain the power to{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              make smarter decisions, create actionable plans, and confidently shape the future of your business
            </Box>
            . Whether you&apos;re refining your market fit, optimizing financial strategies, or redefining your operations, our ecosystem is designed to help you{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              thrive in an ever-evolving landscape
            </Box>
            .
          </Typography>

          <Box sx={{ textAlign: 'center' }}>
            <GetStartedButton 
              variant="contained" 
              size="large" 
              onClick={onGetStarted}
              endIcon={<RocketIcon />}
            >
              GET STARTED
            </GetStartedButton>
          </Box>
        </Box>
      </WelcomeContainer>
    </motion.div>
  );
};

export default WelcomeGuide; 